import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { DEFAULT_GOLD } from "../../constant";

interface CarouselItem {
  image: string;
  caption: string;
}

interface CarouselProps {
  items: CarouselItem[];
  height?: number;
}

const hoverStyle = {
  "&:hover": {
    backgroundColor: "rgba(29, 53, 87, 0.7)",
    color: DEFAULT_GOLD,
  },
};

const iconButtonStyle = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#fff",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  ...hoverStyle,
};

const Carousel: React.FC<CarouselProps> = ({ items, height }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Navigate to the previous slide
  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
  };

  // Navigate to the next slide
  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === items.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 600,
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: 3,
        mx: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          transition: "transform 0.5s ease-in-out",
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {items.map((item, index) => (
          <Box
            key={index}
            data-testid="slide-container"
            sx={{
              width: "100%",
              flex: "0 0 100%",
              height: height ?? 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={item.image}
              alt={item.caption}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                position: "absolute",
                color: "#fff",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                padding: 1,
                borderRadius: 1,
                bottom: 16,
                ...hoverStyle,
              }}
            >
              {item.caption}
            </Typography>
          </Box>
        ))}
      </Box>

      <IconButton
        onClick={handlePrev}
        sx={{
          ...iconButtonStyle,
          left: 16,
        }}
        aria-label="previous slide"
      >
        <ArrowBack />
      </IconButton>
      <IconButton
        onClick={handleNext}
        sx={{
          ...iconButtonStyle,
          right: 16,
        }}
        aria-label="next slide"
      >
        <ArrowForward />
      </IconButton>
    </Box>
  );
};

export default Carousel;
